// File path: src/pages/CompanyInvite.js
// Purpose: Accept company workspace invitations for ManuCalc company accounts.

import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { logFirebaseAccessError } from "../utils/firebasePermissionDebug";
import "./Admin/AdminDashboard.css";

const roleLabels = {
  personal: "Personal",

  owner: "Owner",
  supervisor: "Supervisor",
  machinist: "Machinist",
  estimator: "Estimator",
  viewer: "Viewer",
};

const getInviteByToken = async (token) => {
  const publicInviteRef = doc(db, "publicInvites", token);
  const publicInviteSnap = await getDoc(publicInviteRef);

  if (!publicInviteSnap.exists()) {
    return null;
  }

  return {
    id: publicInviteSnap.id,
    ref: publicInviteRef,
    data: publicInviteSnap.data(),
  };
};

export default function CompanyInvite({ user }) {
  const { token } = useParams();
  const history = useHistory();

  const [invite, setInvite] = useState(null);
  const [companyOwner, setCompanyOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [autoAcceptAttempted, setAutoAcceptAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadInvite = async () => {
      if (!token) {
        setError("Invite token is missing.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const foundInvite = await getInviteByToken(token);

        if (!foundInvite) {
          if (!isMounted) return;
          setError("This company invite could not be found.");
          setInvite(null);
          return;
        }

        if (!isMounted) return;
        setInvite(foundInvite);
        setCompanyOwner(null);
      } catch (loadError) {
        console.error("Failed to load company invite:", loadError);
        if (!isMounted) return;
        setError("Failed to load this invite. Check the link and try again.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadInvite();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const inviteData = invite?.data || {};

  useEffect(() => {
    if (loading || !invite || user?.uid || !token) return;

    const returnPath = encodeURIComponent(`/company-invite/${token}`);
    history.replace(
      `/auth?mode=signup&accountType=companyEmployee&billingBypass=companySeat&inviteToken=${encodeURIComponent(
        token
      )}&companyId=${encodeURIComponent(inviteData.companyId || "")}&companyName=${encodeURIComponent(
        inviteData.companyName || "Company Workspace"
      )}&email=${encodeURIComponent(inviteData.email || "")}&redirect=${returnPath}`
    );
  }, [loading, invite, user, token, history, inviteData.companyId, inviteData.companyName, inviteData.email]);

  const seatSummary = useMemo(() => {
    const seatCount = Number(companyOwner?.seatCount || 1);
    const pendingInvites = Array.isArray(companyOwner?.pendingInvites)
      ? companyOwner.pendingInvites
      : [];
    const teamMembers = Array.isArray(companyOwner?.teamMembers)
      ? companyOwner.teamMembers.filter((member) => member.status !== "deactivated")
      : [];
    const usedSeats =
      1 +
      teamMembers.length +
      pendingInvites.filter((item) => item.status === "pending").length;

    return {
      seatCount,
      usedSeats,
      openSeats: Math.max(0, seatCount - usedSeats),
    };
  }, [companyOwner]);

  const acceptInvite = async () => {
    if (!invite || !inviteData?.companyOwnerUid || !inviteData?.companyId) {
      setError("This invite is missing company data.");
      return;
    }

    if (!user?.uid) {
      setError("Sign in or create an account before accepting this company invite.");
      return;
    }

    if (["accepted", "cancelled"].includes(inviteData.status)) {
      setError("This invite has already been used or is no longer active.");
      return;
    }

    const signedInEmail = String(user.email || "").toLowerCase();
    const invitedEmail = String(inviteData.email || "").toLowerCase();

    if (invitedEmail && signedInEmail && invitedEmail !== signedInEmail) {
      setError(`This invite was sent to ${inviteData.email}. Sign in with that email to accept it.`);
      return;
    }

    try {
      setAccepting(true);
      setError("");
      setMessage("");

      const companyId = inviteData.companyId;
      const inviteToken = inviteData.token || token || invite.id;
      const memberRole = inviteData.role || "viewer";
      const memberData = {
        id: user.uid,
        uid: user.uid,
        name: user.displayName || user.email || "Team Member",
        email: user.email || inviteData.email || "",
        role: memberRole,
        status: "available",
        seatType: "Employee Seat",
        companyId,
        companyName: inviteData.companyName || "Company Workspace",
        companyOwnerUid: inviteData.companyOwnerUid,
        inviteToken,
        currentProject: "",
        currentTask: "",
        hoursWorked: 0,
        hourlyRate: 0,
        joinedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const ownerRef = doc(db, "users", inviteData.companyOwnerUid);

      try {
        await setDoc(
          doc(db, "companies", companyId, "members", user.uid),
          {
            ...memberData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (memberError) {
        logFirebaseAccessError({
          error: memberError,
          path: `companies/${companyId}/members/${user.uid}`,
          operation: "create",
          user,
          role: memberRole,
          companyId,
          context: "CompanyInvite accept member create",
        });
        throw memberError;
      }

      const currentUserRef = doc(db, "users", user.uid);
      const currentUserSnap = await getDoc(currentUserRef);
      const currentUserData = currentUserSnap.exists() ? currentUserSnap.data() : {};

      await setDoc(
        currentUserRef,
        {
          previousAccountType: currentUserData?.accountType || "personal",
          accountType: "company",
          accountBillingType: "companySeat",
          billingExempt: true,
          subscriptionRequired: false,
          subscriptionStatus: "active",
          trialStartDate: null,
          trialEndDate: null,
          role: memberRole,
          companyId,
          companyName: inviteData.companyName || "Company Workspace",
          companyOwnerUid: inviteData.companyOwnerUid,
          adminDashboardEnabled: false,
          companyInviteAcceptedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      try {
        await updateDoc(ownerRef, {
          companyTeamUpdatedAt: serverTimestamp(),
          companyInviteUpdatedAt: serverTimestamp(),
        });
      } catch (ownerUpdateError) {
        console.warn("Employee was connected, but owner dashboard timestamp cleanup failed:", ownerUpdateError);
      }

      try {
        await setDoc(
          invite.ref,
          {
            status: "accepted",
            acceptedAt: serverTimestamp(),
            acceptedByUid: user.uid,
            acceptedByEmail: user.email || inviteData.email || "",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (inviteAcceptError) {
        logFirebaseAccessError({
          error: inviteAcceptError,
          path: `publicInvites/${inviteToken}`,
          operation: "update",
          user,
          role: memberRole,
          companyId,
          context: "CompanyInvite public invite accept",
        });
        throw inviteAcceptError;
      }

      try {
        await setDoc(
          doc(db, "companies", companyId, "invites", inviteToken),
          {
            status: "accepted",
            acceptedAt: serverTimestamp(),
            acceptedByUid: user.uid,
            acceptedByEmail: user.email || inviteData.email || "",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (companyInviteUpdateError) {
        console.warn(
          "Employee was connected, but company invite status cleanup failed:",
          companyInviteUpdateError
        );
      }

      setMessage("Invite accepted. You are now connected to the company workspace.");

      try {
        await setDoc(
          doc(db, "companies", companyId, "activityLog", `activity-${Date.now()}`),
          {
            type: "invite_accepted",
            title: "Employee joined company",
            details: `${memberData.name} joined as ${memberRole}`,
            memberId: user.uid,
            companyId,
            createdAtIso: new Date().toISOString(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (activityError) {
        console.warn("Failed to write invite acceptance activity log:", activityError);
      }

      setTimeout(() => {
        window.location.assign("/dashboard");
      }, 900);
    } catch (acceptError) {
      console.error("Failed to accept company invite:", acceptError);
      setError("Failed to accept this invite. Check Firestore permissions and try again.");
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    if (
      loading ||
      !invite ||
      !user?.uid ||
      accepting ||
      autoAcceptAttempted ||
      inviteData.status !== "pending"
    ) {
      return;
    }

    const signedInEmail = String(user.email || "").toLowerCase();
    const invitedEmail = String(inviteData.email || "").toLowerCase();

    if (invitedEmail && signedInEmail && invitedEmail !== signedInEmail) {
      return;
    }

    setAutoAcceptAttempted(true);
    acceptInvite();
    // acceptInvite updates invite/user state; keeping it out of this effect avoids duplicate invite acceptance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invite, user, accepting, autoAcceptAttempted, inviteData.status, inviteData.email]);

  if (loading) {
    return (
      <main className="admin-dashboard-page">
        <section className="admin-dashboard-empty-state">
          <h1>Loading Invite...</h1>
          <p>Checking the company invite link.</p>
        </section>
      </main>
    );
  }

  if (error && !invite) {
    return (
      <main className="admin-dashboard-page">
        <section className="admin-dashboard-empty-state admin-dashboard-error">
          <span className="admin-dashboard-kicker">Company Invite</span>
          <h1>Invite Not Available</h1>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-dashboard-page">
      <section className="admin-dashboard-empty-state">
        <span className="admin-dashboard-kicker">Company Invite</span>
        <h1>Join {inviteData.companyName || "Company Workspace"}</h1>
        <p>
          You have been invited as a {roleLabels[inviteData.role] || inviteData.role || "team member"}.
          Accepting this invite will connect your ManuCalc account to this company workspace.
        </p>

        <div className="admin-dashboard-billing-box" style={{ marginTop: "24px" }}>
          <div>
            <span>Invite Email</span>
            <strong>{inviteData.email || "Not specified"}</strong>
          </div>
          <div>
            <span>Role</span>
            <strong>{roleLabels[inviteData.role] || inviteData.role || "Viewer"}</strong>
          </div>
          <div>
            <span>Available Seats</span>
            <strong>{seatSummary.openSeats}/{seatSummary.seatCount}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{inviteData.status || "pending"}</strong>
          </div>
        </div>

        {!user?.uid && (
          <p className="admin-dashboard-muted-note">
            Redirecting you to create a free employee account under this company workspace.
          </p>
        )}

        {error && <p className="admin-dashboard-muted-note">{error}</p>}
        {message && <p className="admin-dashboard-muted-note">{message}</p>}

        <button
          type="button"
          className="admin-dashboard-primary-btn active"
          onClick={acceptInvite}
          disabled={!user?.uid || accepting || ["accepted", "cancelled"].includes(inviteData.status)}
        >
          {accepting ? "Connecting Account..." : "Accept Company Invite"}
        </button>
      </section>
    </main>
  );
}
